import { getHrDashboardAvailableStoresOptions } from '@gozoki/api'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useRouterState, useSearch } from '@tanstack/react-router'
import z from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { HrSelectionTitle } from '~/components/routes/hr/planning/dashboard/hr-selection-title'
import { HrSlotProvider, HrSlotTarget } from '~/components/routes/hr/planning/hr-toolbar-slot'
import { HR_PAGE_READ, HR_TAB_ROUTE, HR_TABS, type HrTab } from '~/components/routes/hr/tabs'
import { useFeatureFlagEvaluator } from '~/hooks/useFeatureFlag'
import { withRoutePermissions } from '~/utils/rbac'
import { useAuthorizedTabs, useFirstAuthorizedTab } from '~/utils/rbacTabs'
import { Calendar, CalendarCheck2, LayoutGrid, Users, type LucideIcon } from 'lucide-react'

/**
 * Layout partagé des sous-onglets RH. Depuis le découpage en routes leaf (un tab = une route sous
 * `planning/`), cette route ne porte QUE la topbar commune (titre + barre d'onglets + slots) et les
 * search params PARTAGÉS entre onglets. Chaque route enfant porte ses propres params et son contenu.
 */
const tabValues = [
  'dashboard',
  'calendar',
  'collaborators',
  'attendance',
  'absence-calendar',
] as const

const tabIcon: Record<HrTab, LucideIcon> = {
  dashboard: LayoutGrid,
  calendar: Calendar,
  collaborators: Users,
  attendance: CalendarCheck2,
  'absence-calendar': Calendar,
}

export const Route = createFileRoute('/_authenticated/hr/planning')({
  staticData: {
    permission: HR_PAGE_READ,
  },
  component: RouteComponent,
  validateSearch: z.object({
    // Boutique affichée : partagée par tous les onglets (tête de la sélection mono-boutique).
    storeId: z.number().optional(),
    // Set multi-boutique dédié à l'EXPORT (Pointage / Planning) ; l'affichage reste mono via storeId.
    exportStoreIds: z.array(z.number()).optional(),
  }),
})
withRoutePermissions(Route)

function RouteComponent() {
  const navigate = Route.useNavigate()

  const isFlagEnabled = useFeatureFlagEvaluator()

  const authorizedTabs = useAuthorizedTabs(HR_TABS)
  const firstAuthorizedTab = useFirstAuthorizedTab(authorizedTabs)

  // Onglet actif déduit de l'URL (plus de param `$tab`). Le segment qui suit `planning/`.
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const segment = pathname.split('/hr/planning/')[1]?.split('/')[0]
  const currentTab = tabValues.find((value) => value === segment)

  // Onglet sous flag (Tableau de bord, `dashboard-rh`) masqué tant que son flag est off : son
  // entrée sort de la topbar. Même lecture que la palette ⌘K (`flagKey` de `HR_TABS`).
  const visibleTabs = authorizedTabs.filter((item) => isFlagEnabled(item.flagKey))

  // Un onglet interdit reste affiché (désactivé + tooltip) mais on surligne le premier accessible.
  const activeTab = authorizedTabs.find((item) => item.value === currentTab)?.authorized
    ? currentTab
    : (firstAuthorizedTab ?? currentTab)

  // Titre dynamique du dashboard RH selon le périmètre boutiques (charte Réseau & Boutiques). Seul
  // l'onglet Tableau de bord porte la sélection multi-boutique ; les autres gardent « RH & Planning ».
  const dashboardSearch = useSearch({
    from: '/_authenticated/hr/planning/dashboard',
    shouldThrow: false,
  })
  const isDashboardTab = activeTab === 'dashboard'
  const { data: availableStores = [] } = useQuery({
    ...getHrDashboardAvailableStoresOptions(),
    enabled: isDashboardTab,
  })

  return (
    <HrSlotProvider>
      {/* Header + AppContainer alignés sur le Dashboard Réseau (py-4, titre 32px, mb-4). */}
      <AppHeader className="px-5 py-4 mb-4">
        <div className="flex items-center gap-2">
          <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
            {isDashboardTab && dashboardSearch ? (
              <HrSelectionTitle
                availableStores={availableStores}
                selection={{
                  storeIds: dashboardSearch.storeIds,
                  storeAgeFilter: dashboardSearch.storeAgeFilter,
                  ageStoreIds: dashboardSearch.ageStoreIds,
                }}
              />
            ) : (
              'RH & Planning'
            )}
          </Title>
          {/* Badge de période : portalé par la route dashboard (seule à en avoir la plage). */}
          <HrSlotTarget name="header" className="flex items-center gap-2" />
        </div>
      </AppHeader>

      <AppContainer>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AppTabs
            tabs={visibleTabs.map((item) => ({
              label: item.label,
              icon: tabIcon[item.value],
              value: item.value,
              disabled: !item.authorized,
              tooltip: item.tooltip,
            }))}
            value={activeTab}
            onTabChange={(value) =>
              navigate({ to: HR_TAB_ROUTE[value], search: (prev) => ({ ...prev }) })
            }
          />
          {/* Actions propres au tab (sélecteurs + boutons export) portalées par chaque route leaf. */}
          <HrSlotTarget name="toolbar" className="flex flex-wrap items-center justify-end gap-2" />
        </div>

        {/* Contenu du tab actif + panneaux imbriqués (ex. fiche collaborateur). */}
        <Outlet />
      </AppContainer>
    </HrSlotProvider>
  )
}
